import { cx } from "components/Graphs/utils";
import React from "react";

export interface ApplicationShellProps {
  header: React.ReactNode;
  navigation: React.ReactNode;
  mobileNavigation?: React.ReactNode;
  statusBar?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  mainAriaLabel?: string;
  railWidth?: string;
}

type ShellStyles = React.CSSProperties & {
  "--shell-header-height": string;
  "--shell-rail-width": string;
  "--shell-mobile-navigation-height": string;
  "--shell-status-height": string;
};

export const ApplicationShell: React.FC<ApplicationShellProps> = ({
  header,
  navigation,
  mobileNavigation,
  statusBar,
  children,
  className,
  mainAriaLabel = "Application workspace",
  railWidth = "64px",
}) => {
  const shellStyles: ShellStyles = {
    "--shell-header-height": "56px",
    "--shell-rail-width": railWidth,
    "--shell-mobile-navigation-height": mobileNavigation ? "64px" : "0px",
    "--shell-status-height": statusBar ? "36px" : "0px",
  };

  return (
    <div
      className={cx(
        "relative h-[100dvh] min-h-[30rem] w-full min-w-0 overflow-hidden bg-[#060807] text-primary",
        className,
      )}
      style={shellStyles}
      data-application-shell
    >
      <a
        href="#application-workspace"
        className="fixed left-3 top-2 z-[100] -translate-y-20 rounded-sm bg-highlight px-3 py-2 text-sm font-semibold text-black transition-transform focus:translate-y-0 motion-reduce:transition-none"
      >
        Skip to workspace
      </a>

      <div className="fixed inset-x-0 top-0 z-40 h-[var(--shell-header-height)]">
        {header}
      </div>

      <div className="fixed bottom-[var(--shell-status-height)] left-0 top-[var(--shell-header-height)] z-30 hidden w-[var(--shell-rail-width)] transition-[width] duration-150 motion-reduce:transition-none md:block">
        {navigation}
      </div>

      <main
        id="application-workspace"
        aria-label={mainAriaLabel}
        tabIndex={0}
        className="fixed bottom-[calc(var(--shell-status-height)+var(--shell-mobile-navigation-height))] left-0 right-0 top-[var(--shell-header-height)] min-w-0 overflow-auto overscroll-contain bg-[#070a08] transition-[left] duration-150 motion-reduce:transition-none md:bottom-[var(--shell-status-height)] md:left-[var(--shell-rail-width)]"
      >
        {children}
      </main>

      {mobileNavigation ? (
        <div className="fixed bottom-[var(--shell-status-height)] left-0 right-0 z-40 h-[var(--shell-mobile-navigation-height)] md:hidden">
          {mobileNavigation}
        </div>
      ) : null}

      {statusBar ? (
        <div className="fixed inset-x-0 bottom-0 z-50 h-[var(--shell-status-height)]">
          {statusBar}
        </div>
      ) : null}
    </div>
  );
};

ApplicationShell.displayName = "ApplicationShell";

export default ApplicationShell;
